import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscriber,
  Subscription,
  timer,
} from 'rxjs';
import {
  catchError,
  delayWhen,
  retryWhen,
  scan,
  takeUntil,
} from 'rxjs/operators';
import { WebSocketSubject } from 'rxjs/webSocket';
import { AnalyticsService } from 'src/app/components/logging/analytics.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { WebSocketService } from 'src/app/shared/services/web-socket.service';
import { environment } from 'src/environments/environment';
import ChannelConfig from '../types/channel-config';
import Member from '../types/member';
import { MessageType } from '../types/message-type';
import { SignalServerData } from '../types/signal-server-data';
import { WebsocketParams } from '../types/websocket-params';
import { RvshareAppDetectionService } from './rvshare-app-detection.service';
import { ModeratorWebsocketParamsService } from '../types/moderator-params';
import { LocalStorageHelperService } from 'src/app/shared/services/local-storage-helper.service';

@Injectable({
  providedIn: 'root',
})
export class ScreenSharingService {
  private createPeerConnectionState(displayId: string) {
    return {
      displayId,
      pc: null as RTCPeerConnection | null,
      pendingIceCandidates: [] as RTCIceCandidateInit[],
      remoteDescriptionSet: false,
    };
  }

  socket: WebSocketSubject<SignalServerData>;
  socketSubscription: Subscription;
  channelError: string;
  joinCodeError: string;
  iceConfiguration: RTCConfiguration;
  connecting: boolean = false;
  closing: boolean = false;
  reconnecting: boolean = false;
  selectedDisplayIds: string[] = [];
  displayPeerStates: {
    displayId: string;
    peerConnection: RTCPeerConnection | null;
    remoteDescriptionSet: boolean;
    pendingIceCandidates: RTCIceCandidateInit[];
  }[] = [];
  reconnectAttempts: number = 0;
  mediaStream: MediaStream;
  joinedSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  invitedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  started = 0;
  memberDisplayName = '';
  moderator: boolean;
  memberId: string;
  senderId: string;
  invitedId: string;
  participants: Member[];
  displayParticipants: Member[] = [];
  displayParticipant: Member;
  sessionDisplayIds: string[] = [];
  channelTimeout: ReturnType<typeof setTimeout> = null;
  muted = false;
  volume: ChannelConfig['volume'] = 100;
  quality: ChannelConfig['quality'] = 'high';
  allowOthersToJoin: boolean = false;
  isModerated = false;

  get moderatorCount(): number {
    if (this.participants) {
      return this.participants.filter((item) => item.memberType === 'moderator')
        .length;
    }
    return 0;
  }

  get sharing(): Member | null {
    const member =
      this.participants &&
      this.participants.find((user) => this.isSharingParticipant(user));
    return member ? member : null;
  }

  get memberIsSharing(): boolean {
    return !!this.memberId && this.sharing?.memberId === this.memberId;
  }

  get videoConstraints(): MediaTrackConstraints {
    if (this.rvshareAppDetectionService.shouldDisableVideoQualitySetting()) {
      return { width: {}, height: {} };
    }

    return this.quality === 'high'
      ? { width: { ideal: 1920 }, height: { ideal: 1080 } }
      : this.quality === 'medium'
        ? { width: { ideal: 1280 }, height: { ideal: 720 } }
        : this.quality === 'low'
          ? { width: { ideal: 640 }, height: { ideal: 360 } }
          : { width: {}, height: {} };
  }

  private readonly maxReconnectAttempts = 5; // Adjust this as needed
  private readonly reconnectInterval = 5000; // Adjust this as needed (milliseconds)
  private destroy$ = new Subject<void>();
  private currentWebsocketParams: WebsocketParams;

  constructor(
    private webSocketService: WebSocketService,
    private rvshareAppDetectionService: RvshareAppDetectionService,
    private analyticsFactory: AnalyticsService,
    private userStateService: UserStateService,
    private localStorageHelperService: LocalStorageHelperService,
  ) {}

  private findParticipantIdFromMobileSender(senderId: string): string {
    if (!this.participants || this.participants.length === 0) {
      return '';
    }

    const participant = this.participants.find(
      (member) => member.mobileMemberId === senderId,
    );

    return participant ? participant.memberId : '';
  }

  /**
   * Resolves which display peer connection a signal belongs to. The server
   * sometimes omits `displayId` on answers/ICE; `senderId` then identifies the
   * display participant. Without this, multi-display sessions route every signal
   * to `displayPeerStates[0]` and the second answer hits RTCPeerConnection in
   * `stable` (InvalidStateError).
   */
  private findDisplayPeerStateForSignal(
    displayId?: string,
    senderId?: string,
  ): (typeof this.displayPeerStates)[number] | undefined {
    if (displayId) {
      const byDisplayId = this.displayPeerStates.find(
        (c) => c.displayId === displayId,
      );
      if (byDisplayId) {
        return byDisplayId;
      }
    }
    if (senderId) {
      const bySenderAsDisplayId = this.displayPeerStates.find(
        (c) => c.displayId === senderId,
      );
      if (bySenderAsDisplayId) {
        return bySenderAsDisplayId;
      }
      const displayMember = this.displayParticipants.find(
        (m) => m.memberId === senderId || m.displayId === senderId,
      );
      if (displayMember?.displayId) {
        return this.displayPeerStates.find(
          (c) => c.displayId === displayMember.displayId,
        );
      }
    }
    if (this.displayPeerStates.length === 1) {
      return this.displayPeerStates[0];
    }
    return undefined;
  }

  private associateMobileIds(members?: Member[]): void {
    if (!members) {
      return;
    }

    members.forEach((member) => {
      if (
        !['mobile', 'mobile_ios', 'mobile_android'].includes(member.memberType)
      ) {
        return;
      }

      const participant = this.participants.find(
        (participant) => participant.memberId === member.appsMemberId,
      );

      if (!participant) {
        return;
      }

      participant.mobileMemberId = member.memberId;
    });
  }

  private isSharingParticipant(member: Member): boolean {
    if (!member) {
      return false;
    }

    return (
      !!member.sharing ||
      (!!this.senderId &&
        [member.memberId, member.appsMemberId, member.mobileMemberId].includes(
          this.senderId,
        ))
    );
  }

  private filterParticipants(members?: Member[]): void {
    if (members) {
      this.participants = members
        .filter((member) =>
          ['moderator', 'streamer'].includes(member.memberType),
        )
        .sort((a) => (a.memberType === 'moderator' ? -1 : 0));
    }

    this.displayParticipants = (members ?? []).filter(
      (member) => member.memberType === 'display',
    );
    this.displayParticipant = this.displayParticipants.find(
      (member) => member.memberType === 'display',
    );

    this.associateMobileIds(members);

    const activeSharer = this.participants?.find((member) => member.sharing);
    if (activeSharer) {
      this.senderId = activeSharer.memberId;
    }
  }

  private clearSharingParticipantState(senderId?: string): void {
    if (!this.participants?.length) {
      return;
    }

    const activeSenderId = senderId || this.senderId;
    this.participants.forEach((member) => {
      if (
        member.sharing &&
        (!activeSenderId ||
          [
            member.memberId,
            member.appsMemberId,
            member.mobileMemberId,
          ].includes(activeSenderId))
      ) {
        member.sharing = false;
      }
    });
  }

  private getTargetDisplayIds(): string[] {
    const displayIds =
      this.sessionDisplayIds.length > 0
        ? this.sessionDisplayIds
        : this.currentWebsocketParams?.displayIds?.length
          ? this.currentWebsocketParams.displayIds
          : this.selectedDisplayIds.length > 0
            ? this.selectedDisplayIds
            : this.currentWebsocketParams?.displayId
              ? [this.currentWebsocketParams.displayId]
              : [];

    return [...new Set(displayIds.filter(Boolean))];
  }

  private restartStreamForDisplay(displayId: string): void {
    this.clearChannelTimeout();
    this.closePeerConnection(displayId);
    this.stream([displayId]);
  }

  private updateConfig(config: ChannelConfig): void {
    this.volume = config?.volume ?? this.volume;
    this.quality = config?.quality ?? this.quality;
  }

  join(websocketParams: WebsocketParams): void {
    console.log('Joining channel with join code: ', websocketParams.joinCode);

    this.channelError = '';
    this.joinCodeError = '';
    this.memberDisplayName = websocketParams.memberDisplayName;
    this.connecting = true;
    this.closing = false;
    this.moderator = websocketParams.isModerator();
    if (this.moderator) {
      this.isModerated = true;
    }

    this.currentWebsocketParams = websocketParams;

    this.connect().subscribe({
      next: (data) => {
        console.log('streamer onmessage', data);

        switch (data.type) {
          case MessageType.JOINED_CHANNEL:
            console.log('Joined a new channel', data);
            this.channelError = '';
            this.joinCodeError = '';
            this.iceConfiguration = data.iceConfiguration;
            this.filterParticipants(data.members);
            this.sessionDisplayIds = data.displayIds?.length
              ? data.displayIds
              : data.displayId
                ? [data.displayId]
                : this.sessionDisplayIds;
            if (this.sessionDisplayIds.length > 0) {
              this.selectedDisplayIds = [...this.sessionDisplayIds];
            }
            this.updateConfig(data.config);
            this.isModerated = data.screenSharingMode === 'moderated';
            this.allowOthersToJoin = data.allowOthersToJoin;

            if (this.senderId && this.memberId === this.senderId) {
              this.senderId = data.memberId;
            }

            this.memberId = data.memberId;

            this.joinedSubject.next(MessageType.JOINED_CHANNEL);

            if (this.rvshareAppDetectionService.isScreenShareApp()) {
              const withStart = this.isModerated ? '' : 'AndStart';
              this.connecting = !!withStart;
              const deepLinkParams = new URLSearchParams();
              if (data.displayId) {
                deepLinkParams.set('displayId', data.displayId);
              }
              if (data.displayIds?.length) {
                deepLinkParams.set('displayIds', data.displayIds.join(','));
              }
              if (data.sessionId) {
                deepLinkParams.set('sessionId', data.sessionId);
              }
              if (data.memberId) {
                deepLinkParams.set('appsMemberId', data.memberId);
              }

              this.rvshareAppDetectionService.issueDeepLink(
                `join${withStart}?${deepLinkParams.toString()}`,
              );
              return;
            }

            this.connecting = false;
            this.reconnecting = false;
            this.reconnectAttempts = 0;

            if (this.channelTimeout && this.displayParticipant) {
              console.log('Restarting stream');
              this.stream();

              return;
            }

            if (!this.isModerated && !this.mediaStream) {
              this.start();
            }

            break;

          case MessageType.INVALID_CHANNEL:
            console.warn('Invalid channel: ', data);
            if (this.moderator) {
              this.channelError =
                'Unable to start a moderated session on display ' +
                websocketParams.displayId +
                '.';
              this.joinedSubject.next(MessageType.ERROR);
            } else {
              this.joinCodeError =
                'Invalid join code: ' + websocketParams.joinCode + '.';
            }
            this.connecting = false;
            this.reset();
            break;

          case MessageType.ICECANDIDATE:
            if (data.memberId === this.memberId) {
              const displayPeerState = this.findDisplayPeerStateForSignal(
                data.displayId,
                data.senderId,
              );

              if (!displayPeerState) {
                console.warn(
                  'ICE candidate: could not resolve display peer (displayId=%s, senderId=%s).',
                  data.displayId,
                  data.senderId,
                );
                break;
              }

              if (displayPeerState.remoteDescriptionSet) {
                displayPeerState.peerConnection?.addIceCandidate(
                  data.candidate,
                );
              } else {
                console.log(
                  `Queuing ICE candidate (remote description not set yet) for display ${data.displayId}.`,
                );
                displayPeerState.pendingIceCandidates.push(data.candidate);
              }
            }
            break;

          case MessageType.OFFER:
            this.senderId =
              this.findParticipantIdFromMobileSender(data.senderId) ||
              data.senderId;
            break;

          case MessageType.ANSWER:
            console.log('Received answer');
            if (this.moderator && !this.senderId && this.invitedId) {
              this.senderId = this.invitedId;
            }
            this.invitedId = '';
            this.channelError = '';
            this.connecting = false;
            this.clearChannelTimeout();
            if (this.senderId === this.memberId) {
              (async () => {
                const displayPeerState = this.findDisplayPeerStateForSignal(
                  data.displayId,
                  data.senderId,
                );
                const peerConnection = displayPeerState?.peerConnection;

                if (!displayPeerState || !peerConnection) {
                  console.warn(
                    'Answer: could not resolve display peer (displayId=%s, senderId=%s).',
                    data.displayId,
                    data.senderId,
                  );
                  return;
                }

                const signalingState = peerConnection.signalingState;
                if (signalingState && signalingState !== 'have-local-offer') {
                  if (signalingState === 'stable') {
                    console.warn(
                      'Skipping answer: peer for display %s is already stable (duplicate or misrouted SDP).',
                      displayPeerState.displayId,
                    );
                  }
                  return;
                }

                try {
                  await peerConnection.setRemoteDescription({
                    type: data.type as RTCSdpType,
                    sdp: data.sdp ?? '',
                  });
                } catch (e) {
                  console.error(
                    'setRemoteDescription failed for display',
                    displayPeerState.displayId,
                    e,
                  );
                  return;
                }

                displayPeerState.remoteDescriptionSet = true;
                console.log(
                  `Flushing ${displayPeerState.pendingIceCandidates.length} queued ICE candidates for display ${displayPeerState.displayId}.`,
                );
                for (const candidate of displayPeerState.pendingIceCandidates) {
                  await peerConnection.addIceCandidate(candidate);
                }
                displayPeerState.pendingIceCandidates = [];
              })();
            }
            if (this.senderId === this.memberId || this.moderator) {
              this.joinedSubject.next(MessageType.ANSWER);
            }
            break;

          case MessageType.NEW_MEMBER:
            let streaming = this.displayPeerStates.some(
              (c) => !!c.peerConnection,
            );
            console.log('New member joined', data, 'streaming', streaming);

            this.filterParticipants(data.members);
            const rejoinedDisplay = this.displayParticipants.find(
              (member) => member.memberId === data.senderId,
            );

            if (streaming && rejoinedDisplay?.displayId) {
              this.restartStreamForDisplay(rejoinedDisplay.displayId);
            } else if (
              streaming &&
              this.displayParticipant &&
              data.senderId === this.displayParticipant.memberId
            ) {
              this.restartStream();
            }

            break;

          case MessageType.MODERATOR_NOT_ALLOWED:
            this.channelError =
              'This display is not configured for screen share moderation. Please check your display settings.';
            this.closing = true;
            this.joinedSubject.next(MessageType.ERROR);
            break;

          case MessageType.MODERATOR_NOT_AUTHENTICATED:
            this.channelError =
              'Authentication failed. Please log in and try again.';
            this.closing = true;
            this.joinedSubject.next(MessageType.ERROR);
            break;

          case MessageType.MEMBER_DISCONNECTED:
            this.filterParticipants(data.members);
            break;

          case MessageType.REMOVED_FROM_CHANNEL:
            this.channelError =
              data.reason ?? 'A moderator has removed you from the room.';
            this.closing = true;
            this.joinedSubject.next(MessageType.ERROR);
            break;

          case MessageType.INVITE_SHARING:
            this.invitedSubject.next(true);
            break;

          case MessageType.STOP_SHARING:
            this.invitedSubject.next(false);
            this.clearSharingParticipantState(
              data.memberId || data.senderId || this.senderId,
            );
            this.closeStream();
            break;

          case MessageType.CLIENT_STREAM_CLOSED:
            this.clearSharingParticipantState(data.senderId);
            this.closeStream();
            if (data.senderId === this.invitedId) {
              this.invitedId = '';
            }
            break;
          case MessageType.DISPLAY_INTERRUPTED:
            this.channelError =
              'The screen sharing session has been interrupted.';
            this.closing = true;
            this.joinedSubject.next(MessageType.ERROR);
            break;
          case MessageType.CONNECTION_REJECTED:
            this.closing = true;
            this.connecting = false;
            this.channelError =
              'The connection was rejected because the session already has a streamer connected.';
            this.joinedSubject.next(MessageType.ERROR);
            break;
          case MessageType.CONNECTION_REJECTED_OTHERS_JOIN:
            this.closing = true;
            this.connecting = false;
            this.channelError =
              "The connection was rejected because the session doesn't allow others to join.";
            this.joinedSubject.next(MessageType.ERROR);
            break;
          case MessageType.VOLUME_MUTED:
            this.muted = true;
            break;
          case MessageType.CHANNEL_CONFIG:
            this.updateConfig(data.config);
            if (this.memberIsSharing && data.config?.quality) {
              this.applyVideoResolution();
            }
            break;
          case MessageType.REQUEST_PIN:
            this.joinedSubject.next(MessageType.REQUEST_PIN);
            this.connecting = false;
            this.closing = true;
            break;
          case MessageType.INVALID_SECURITY_PIN:
            this.joinCodeError = 'The PIN you entered is incorrect.';
            this.joinedSubject.next(MessageType.ERROR);
            this.connecting = false;
            this.closing = true;
            break;
          case MessageType.REQUEST_AUTHENTICATION:
            this.joinedSubject.next(MessageType.REQUEST_AUTHENTICATION);
            this.connecting = false;
            this.closing = true;
            break;
          case MessageType.INVALID_AUTHENTICATION:
            this.channelError =
              'There was something wrong with your authentication. Please try again.';
            this.joinedSubject.next(MessageType.ERROR);
            this.connecting = false;
            this.closing = true;
            break;
          case MessageType.INVALID_AUTHENTICATION_COMPANY:
            this.channelError =
              "You're not a user of the required company. Please contact your company's system administrator to add you as a user.";
            this.joinedSubject.next(MessageType.ERROR);
            this.connecting = false;
            this.closing = true;
            break;
          default:
            console.log('Unknown message', data);
        }
      },
      error: (e) => {
        console.log('WebSocket error:', e);
        this.channelError =
          'Connection could not be established. Please check your network connection.';
        this.joinedSubject.next(MessageType.ERROR);
        this.connecting = false;
        this.reset();
      },
      complete: () => {
        // Check if the socket is closed intentionally
        if (!this.closing) {
          this.channelError = 'The session has ended.';
          this.joinedSubject.next(MessageType.ERROR);
          this.closing = true;
        }

        console.log('WebSocket connection closed');
      },
    });
  }

  private closeStream() {
    this.senderId = '';
    this.joinedSubject.next(MessageType.CLIENT_STREAM_CLOSED);
  }

  connect(): Observable<SignalServerData> {
    this.destroy$ = new Subject<void>();

    return new Observable<SignalServerData>((subscriber) => {
      if (this.reconnecting && this.currentWebsocketParams.token) {
        this.currentWebsocketParams.token =
          this.userStateService.getAccessToken()?.access_token ||
          this.currentWebsocketParams.token;
      }
      const url = `${environment.SCREEN_SHARING_SERVICE_URL}?${this.currentWebsocketParams.asQueryParams()}`;
      this.connectToWebSocket(url, subscriber);
    }).pipe(
      takeUntil(this.destroy$),
      retryWhen((errors) =>
        errors.pipe(
          delayWhen(() => {
            console.log(
              `Retrying WebSocket connection in ${this.reconnectInterval}ms...`,
            );
            return timer(this.reconnectInterval * this.reconnectAttempts);
          }),
          scan(
            (retryCount, err) => {
              if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                throw err; // Throw error to stop retrying
              } else {
                console.log(
                  `WebSocket reconnecting attempt ${this.reconnectAttempts + 1}`,
                );
                this.reconnectAttempts++;
                return this.reconnectAttempts;
              }
            },
            (this.reconnectAttempts = 0),
          ), // Initial retry count
          catchError((err) => {
            console.error(
              `WebSocket retry attempts exhausted (${this.maxReconnectAttempts}).`,
            );
            throw err; // Rethrow the error to propagate it to the subscription's error handler
          }),
        ),
      ),
    );
  }

  private connectToWebSocket(
    url: string,
    subscriber: Subscriber<SignalServerData>,
  ): void {
    this.socket = this.webSocketService.webSocket({
      url,
      openObserver: {
        next: () => {
          console.log('WebSocket connection established');
        },
      },
      closeObserver: {
        next: () => {
          // Check if the socket is closed intentionally
          if (!this.closing) {
            // Try reconnecting once
            if (!this.reconnecting) {
              console.warn('Socket disconnected, reconnecting.');

              subscriber.error(new Error('Socket disconnected, reconnecting.'));

              this.reconnecting = true;

              return;
            }

            console.error('Socket disconnected, retrying failed.');
          }

          if (this.mediaStream) {
            this.stop();
          }
          this.destroy();
          // Always close subscriber, unless reconnecting
          subscriber.complete();
        },
      },
    });
    this.socketSubscription = this.socket.subscribe({
      next: (data) => subscriber.next(data),
      error: (err) => {
        console.log('WebSocket error:', err);

        subscriber.error(err);
      },
      complete: () => () => {
        // Do nothing; wait for reconnect to complete
      },
    });
  }

  destroy(): void {
    if (this.destroy$) {
      this.destroy$.next();
      this.destroy$.complete();
    }
  }

  start() {
    this.connecting = true;

    if (this.rvshareAppDetectionService.isScreenShareApp()) {
      this.rvshareAppDetectionService.issueDeepLink('start');
      return;
    }

    const mediaOptions = {
      video: this.videoConstraints,
      audio: {
        autoGainControl: false,
        echoCancellation: false,
        noiseSuppression: false,
      },
    };

    navigator.mediaDevices
      .getDisplayMedia(mediaOptions)
      .then((mediaStream: MediaStream) => {
        this.mediaStream = mediaStream;
        this.senderId = this.memberId;
        this.joinedSubject.next(MessageType.ANSWER);

        mediaStream.getTracks().forEach((track) => {
          track.addEventListener('ended', (e) => {
            console.log('track removed', e);
            this.closeStream();
            this.closePeerConnection();
            this.stopAll();
          });
        });

        this.stream();
      })
      .catch((e) => {
        console.log('getDisplayMedia error: ', e);
        this.stop();
        this.joinedSubject.next(MessageType.JOINED_CHANNEL);
      })
      .finally(() => {
        this.connecting = false;
        const now = new Date();
        this.started = now.getTime();
        this.analyticsFactory.track('Screen Sharing Started', {
          timestamp: now.toISOString(),
        });
      });
  }

  changeVolume(value: number) {
    this.socket.next({
      type: MessageType.CHANNEL_CONFIG,
      config: { volume: value },
    });
  }

  applyVideoResolution() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        if (track.kind === 'video') {
          track.applyConstraints(this.videoConstraints);
        }
      });
    }
  }

  changeQuality(value: ChannelConfig['quality']) {
    this.quality = value;
    if (this.moderator) {
      this.socket.next({
        type: MessageType.CHANNEL_CONFIG,
        config: { quality: value },
      });
    }
    if (this.memberIsSharing) {
      this.applyVideoResolution();
    }
  }

  changeAllowOthersToJoin(value: boolean) {
    this.allowOthersToJoin = value;
    this.socket?.next({
      type: MessageType.CHANNEL_CONFIG,
      allowOthersToJoin: value,
    });
  }

  interrupt() {
    this.channelError = 'The connection was interrupted, please try again.';
    this.closePeerConnection();
    if (this.isModerated) {
      this.resetChannel();
    } else {
      this.reset();
    }
  }

  clearChannelTimeout() {
    clearTimeout(this.channelTimeout);
    this.channelTimeout = null;
  }

  restartStream() {
    console.log('Restarting stream');

    this.closePeerConnection();
    this.stream();
  }

  stream(displayIds: string[] = this.getTargetDisplayIds()) {
    if (
      this.rvshareAppDetectionService.isScreenShareApp() ||
      !this.mediaStream
    ) {
      return;
    }

    const targetDisplayIds = [...new Set(displayIds.filter(Boolean))];
    if (targetDisplayIds.length === 0) {
      return;
    }

    const targetDisplayIdSet = new Set(targetDisplayIds);
    targetDisplayIds.forEach((displayId) => {
      if (
        !this.displayPeerStates.find((conn) => conn.displayId === displayId)
      ) {
        this.displayPeerStates.push({
          displayId,
          peerConnection: null,
          remoteDescriptionSet: false,
          pendingIceCandidates: [],
        });
      }
    });

    this.displayPeerStates
      .filter((conn) => targetDisplayIdSet.has(conn.displayId))
      .forEach((displayPeerState) => {
        displayPeerState.remoteDescriptionSet = false;
        displayPeerState.pendingIceCandidates = [];
        displayPeerState.peerConnection?.close();
        displayPeerState.peerConnection = new RTCPeerConnection(
          this.iceConfiguration,
        );
        displayPeerState.peerConnection.addEventListener(
          'connectionstatechange',
          (event?: Event) => {
            const pc = displayPeerState.peerConnection;
            const connectionState = pc?.connectionState;

            console.log(
              'peerConnection.connectionstatechange',
              pc?.connectionState,
              {
                connectionState: pc?.connectionState,
                displayParticipantMemberId: this.displayParticipant?.memberId,
                iceConnectionState: pc?.iceConnectionState,
                signalingState: pc?.signalingState,
                iceGatheringState: pc?.iceGatheringState,
                event,
              },
            );

            switch (connectionState) {
              case 'new':
              case 'connecting':
              case 'connected':
                break;
              case 'disconnected':
              case 'failed':
                this.closePeerConnection(displayPeerState.displayId);
                this.channelError =
                  'The connection was interrupted, sharing will resume when the display reconnects.';
                this.clearChannelTimeout();
                this.channelTimeout = setTimeout(
                  () => this.interrupt(),
                  180_000,
                );
                break;
              case 'closed':
                this.closePeerConnection(displayPeerState.displayId);
                break;
            }
          },
          false,
        );

        displayPeerState.peerConnection.addEventListener(
          MessageType.ICECANDIDATE,
          (e) => {
            const candidate = e.candidate
              ? {
                  candidate: e.candidate.candidate,
                  sdpMid: e.candidate.sdpMid,
                  sdpMLineIndex: e.candidate.sdpMLineIndex,
                }
              : null;
            this.socket.next({
              type: MessageType.ICECANDIDATE,
              candidate,
              displayId: displayPeerState.displayId,
            });
          },
        );

        this.mediaStream
          .getTracks()
          .forEach((track) =>
            displayPeerState.peerConnection.addTrack(track, this.mediaStream),
          );

        if (!displayPeerState.peerConnection.createOffer) {
          return;
        }
        displayPeerState.peerConnection
          .createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true })
          .then(async (offer) => {
            if (!offer) {
              return;
            }
            if (displayPeerState.peerConnection?.setLocalDescription) {
              await displayPeerState.peerConnection.setLocalDescription(offer);
            }
            this.socket.next({
              type: MessageType.OFFER,
              sdp: offer.sdp,
              displayId: displayPeerState.displayId,
            });
          });
      });
  }

  remove(memberId: string) {
    if (this.senderId === memberId) {
      this.stopSharing();
    }
    setTimeout(() => {
      this.socket.next({
        type: MessageType.REMOVE_FROM_CHANNEL,
        memberId,
        reason: 'A moderator has removed you from the room.',
      });
    }, 1000);
  }

  invite(memberId: string) {
    this.invitedId = memberId;
    this.socket.next({ type: MessageType.INVITE_SHARING, memberId });
  }

  stopSharing() {
    this.socket.next({
      type: MessageType.STOP_SHARING,
      memberId: this.senderId,
    });
  }

  pause() {
    this.closePeerConnection();
    this.socket?.next({ type: MessageType.CLIENT_STREAM_PAUSED });
  }

  stop() {
    this.socket?.next({
      type: MessageType.STOP_SHARING,
      memberId: this.senderId || this.memberId,
    });

    this.senderId = '';
    this.invitedId = '';

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    this.closePeerConnection();
    this.stopAll();

    if (this.rvshareAppDetectionService.isScreenShareApp()) {
      this.rvshareAppDetectionService.issueDeepLink('stop');
    }
  }

  leave(endModeratedSession: boolean = false) {
    this.closing = true;
    if (endModeratedSession) {
      this.socket?.next({
        type: MessageType.RETURN_TO_STANDARD,
        reason: 'A moderator closed the session.',
      });
      this.localStorageHelperService.remove(
        ModeratorWebsocketParamsService.MODERATOR_SESSION_KEY,
      );
      this.allowOthersToJoin = false;
    }
    this.participants = [];
    this.joinedSubject.next(MessageType.CHANNEL_CLOSED);

    if (this.rvshareAppDetectionService.isScreenShareApp()) {
      this.rvshareAppDetectionService.issueDeepLink('leave');
    }

    setTimeout(() => {
      this.socketSubscription?.unsubscribe();
      if (this.socket) this.socket.complete();
    }, 500);
  }

  reset(endModeratedSession: boolean = false) {
    this.closing = true;
    this.stop();
    this.destroy();
    this.muted = false;

    this.leave(endModeratedSession);
  }

  resetChannel() {
    this.socket.next({ type: MessageType.RESET_CHANNEL });
    this.reset(!!this.moderator);
  }

  closePeerConnection(displayIds?: string | string[]) {
    const targetDisplayIds = displayIds
      ? new Set(Array.isArray(displayIds) ? displayIds : [displayIds])
      : null;
    let hadConnection = false;
    this.displayPeerStates = this.displayPeerStates.filter(
      (displayPeerState) => {
        if (
          targetDisplayIds &&
          !targetDisplayIds.has(displayPeerState.displayId)
        ) {
          return true;
        }

        if (displayPeerState.peerConnection) {
          hadConnection = true;
          try {
            displayPeerState.peerConnection.close();
            displayPeerState.pendingIceCandidates = [];
            displayPeerState.remoteDescriptionSet = false;
          } catch (e) {
            console.error(
              'Error closing peerConnection for display',
              displayPeerState.displayId,
              e,
            );
          }
          displayPeerState.peerConnection = null;
        }

        return targetDisplayIds ? false : false;
      },
    );
    if (hadConnection && !targetDisplayIds) {
      const now = new Date();
      this.analyticsFactory.track('Screen Sharing Ended', {
        timestamp: now.toISOString(),
        duration: Math.round((now.getTime() - this.started) / 1000),
      });
    }
  }

  clearErrors() {
    this.channelError = '';
  }

  stopAll() {
    this.mediaStream?.getTracks().forEach((t) => t.stop());
    this.mediaStream = null;
    this.closePeerConnection();
    this.displayPeerStates = [];
  }
}
